import React, { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Save, FileSpreadsheet, Upload, TrendingUp, TrendingDown, Layers } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

// Default seed tiers when an event has none yet (migrates from legacy single-fee fields)
const seedTiersFromEvent = (event) => {
  const tiers = event.fee_tiers && event.fee_tiers.length ? event.fee_tiers : null;
  if (tiers) return tiers.map((t) => ({ ...t, tier_id: t.tier_id || `tier_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` }));
  // Legacy single tier
  return [{
    tier_id: `tier_${Date.now()}_0`,
    label: "Delegate Fee",
    fee_amount: event.delegate_fee || 0,
    student_count: event.expected_delegates || 0,
    kaimera_fee_override: null,
  }];
};

export default function BudgetTab({ event, reload }) {
  const [budget, setBudget] = useState(null);
  // Projected ledgers (the plan)
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  // Real-time ledgers (actuals) — a completely INDEPENDENT set that only
  // appears when the "Real time" toggle is on. Adding/deleting in
  // real-time mode never touches the projected lists and vice-versa.
  const [liveIncomes, setLiveIncomes] = useState([]);
  const [liveExpenses, setLiveExpenses] = useState([]);
  const [tiers, setTiers] = useState(seedTiersFromEvent(event));
  const [kaimeraFee, setKaimeraFee] = useState(event.kaimera_fee_per_delegate || 0);
  const fileRef = useRef(null);
  const [editingIncome, setEditingIncome] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  // Real-time toggle — when ON we swap the projected fee-tier revenue /
  // Kaimera numbers with actuals sourced from `receipts` + `journal_entries`
  // AND the Incomes/Expenses tables swap to their live counterparts.
  const [realtime, setRealtime] = useState(false);
  const [live, setLive] = useState(null);

  const load = async () => {
    const r = await api.get(`/events/${event.event_id}/budget`);
    setBudget(r.data);
    setIncomes(r.data.incomes || []);
    setExpenses(r.data.expenses || []);
    setLiveIncomes(r.data.live_incomes || []);
    setLiveExpenses(r.data.live_expenses || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [event.event_id]);

  // Fetch real-time numbers whenever the toggle flips on OR when the event
  // changes. Keeps a small cache so toggling off → on again inside the same
  // session doesn't blank the widget while it re-fetches.
  useEffect(() => {
    if (!realtime) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/events/${event.event_id}/budget/realtime`);
        if (!cancelled) setLive(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Real-time fetch failed");
      }
    })();
    return () => { cancelled = true; };
  }, [realtime, event.event_id]);

  const persist = async (nextInc, nextExp) => {
    // Route the write to the projected keys or the live keys based on
    // which mode the user is editing in. Absent keys are ignored server-
    // side so the two ledgers stay decoupled.
    const body = realtime
      ? { live_incomes: nextInc, live_expenses: nextExp }
      : { incomes: nextInc, expenses: nextExp };
    await api.put(`/events/${event.event_id}/budget`, body);
  };

  // Active arrays — what the ledgers below actually render + write to.
  const activeIncomes = realtime ? liveIncomes : incomes;
  const activeExpenses = realtime ? liveExpenses : expenses;
  const setActiveIncomes = realtime ? setLiveIncomes : setIncomes;
  const setActiveExpenses = realtime ? setLiveExpenses : setExpenses;

  const addItem = async (kind, item) => {
    const row = {
      item_id: `it_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: item.label.trim(),
      quantity: parseFloat(item.quantity) || 1,
      amount: parseFloat(item.amount) || 0,
      notes: item.notes || "",
      kind,
    };
    if (!row.label) return toast.error("Name is required");
    if (row.amount <= 0) return toast.error("Value must be greater than 0");
    const nextInc = kind === "income" ? [...activeIncomes, row] : activeIncomes;
    const nextExp = kind === "expense" ? [...activeExpenses, row] : activeExpenses;
    if (kind === "income") setActiveIncomes(nextInc); else setActiveExpenses(nextExp);
    try { await persist(nextInc, nextExp); toast.success(`${kind === "income" ? "Income" : "Expense"} added`); }
    catch { toast.error("Save failed"); }
  };

  const updateItem = async (kind, idx, patch) => {
    const arr = kind === "income" ? [...activeIncomes] : [...activeExpenses];
    arr[idx] = { ...arr[idx], ...patch };
    if (kind === "income") setActiveIncomes(arr); else setActiveExpenses(arr);
    try { await persist(kind === "income" ? arr : activeIncomes, kind === "expense" ? arr : activeExpenses); }
    catch { toast.error("Save failed"); }
  };

  const delItem = async (kind, idx) => {
    const arr = (kind === "income" ? activeIncomes : activeExpenses).filter((_, i) => i !== idx);
    if (kind === "income") setActiveIncomes(arr); else setActiveExpenses(arr);
    await persist(kind === "income" ? arr : activeIncomes, kind === "expense" ? arr : activeExpenses);
  };

  // ---- Fee tier helpers ----
  const addTier = () => {
    setTiers([...tiers, {
      tier_id: `tier_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: "",
      fee_amount: 0,
      student_count: 0,
      kaimera_fee_override: null,
    }]);
  };
  const updateTier = (idx, patch) => {
    const next = [...tiers];
    next[idx] = { ...next[idx], ...patch };
    setTiers(next);
  };
  const delTier = (idx) => {
    if (tiers.length === 1) return toast.error("At least one fee tier is required");
    setTiers(tiers.filter((_, i) => i !== idx));
  };

  const saveDelegateConfig = async () => {
    // Sanitize tiers before send
    const clean = tiers.map((t) => ({
      tier_id: t.tier_id,
      label: (t.label || "").trim() || "Untitled tier",
      fee_amount: parseFloat(t.fee_amount) || 0,
      student_count: parseInt(t.student_count) || 0,
      kaimera_fee_override: t.kaimera_fee_override === "" || t.kaimera_fee_override == null ? null : parseFloat(t.kaimera_fee_override),
    }));
    // Legacy mirror: keep expected_delegates & delegate_fee for old widgets
    const totalDelegates = clean.reduce((s, t) => s + t.student_count, 0);
    const weightedFee = totalDelegates > 0 ? clean.reduce((s, t) => s + t.fee_amount * t.student_count, 0) / totalDelegates : 0;
    try {
      const body = {
        fee_tiers: clean,
        expected_delegates: totalDelegates,
        delegate_fee: Math.round(weightedFee),
        kaimera_fee_per_delegate: parseFloat(kaimeraFee) || 0,
      };
      try {
        await api.patch(`/events/${event.event_id}`, body);
      } catch (err) {
        const d = err?.response?.data?.detail;
        if (err?.response?.status === 409 && d?.code === "kaimera_fee_change_needs_confirm") {
          const ok = window.confirm(`${d.message}\n\nProceed with the Kaimera fee change?`);
          if (!ok) return;
          await api.patch(`/events/${event.event_id}`, { ...body, force_kaimera_fee_change: true });
        } else {
          throw err;
        }
      }
      await api.post(`/events/${event.event_id}/workflow/budget?value=true`);
      toast.success("Fee tiers saved & budget marked complete");
      load();
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to save");
    }
  };

  const exportExcel = () => {
    const token = localStorage.getItem("munners_token");
    // Cache-bust so the freshest data is downloaded — browser caches xlsx URLs aggressively otherwise.
    window.open(`${BACKEND_URL}/api/events/${event.event_id}/budget.xlsx?t=${token}&_=${Date.now()}`, "_blank");
  };

  const importExcel = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/events/${event.event_id}/budget/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIncomes(r.data.incomes || []);
      setExpenses(r.data.expenses || []);
      const errs = r.data.import_errors?.length || 0;
      if (errs) toast.warning(`Imported with ${errs} row error(s) — check items highlighted red`);
      else toast.success("Budget imported from Excel");
      load();
    } catch (e) { toast.error("Import failed: " + (e?.response?.data?.detail || e.message)); }
    if (fileRef.current) fileRef.current.value = "";
  };

  if (!budget) return <div className="text-slate-400">Loading…</div>;

  // Totals below reflect the ACTIVE ledger (projected when toggle OFF,
  // live when toggle ON) — that way the four Stat cards + the ledger
  // "Total" rows always agree with what the user is editing.
  const incTotal = activeIncomes.reduce((s, i) => s + (i.amount * i.quantity), 0);
  const expTotal = activeExpenses.reduce((s, i) => s + (i.amount * i.quantity), 0);
  const totalDelegates = tiers.reduce((s, t) => s + (parseInt(t.student_count) || 0), 0);
  const delegateRev = tiers.reduce((s, t) => s + (parseFloat(t.fee_amount) || 0) * (parseInt(t.student_count) || 0), 0);
  const kaimera = tiers.reduce((s, t) => {
    const per = (t.kaimera_fee_override === "" || t.kaimera_fee_override == null) ? (parseFloat(kaimeraFee) || 0) : (parseFloat(t.kaimera_fee_override) || 0);
    return s + per * (parseInt(t.student_count) || 0);
  }, 0);

  // Toggle-driven stat values. When Real-time is ON, we swap the projected
  // delegate revenue + Kaimera with actuals from receipts / journal legs,
  // use the actual paid-delegate headcount AND the totals reflect the
  // separate real-time incomes/expenses tables.
  const useLive = realtime && live;
  const shownDelegateRev = useLive ? live.delegate_revenue : delegateRev;
  const shownKaimera = useLive ? live.kaimera_retained : kaimera;
  const shownDelegateCount = useLive ? live.total_delegates : totalDelegates;
  const totalRev = incTotal + shownDelegateRev;
  const totalExp = expTotal + shownKaimera;
  const net = totalRev - totalExp;

  return (
    <div data-testid="budget-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display font-bold text-xl">Budget Calculator</h3>
          <p className="text-sm text-slate-500">Add fee tiers (School / Individual / Host / Custom). Kaimera fee is segregated per delegate.</p>
        </div>
        <div className="flex items-center gap-3">
          <label
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer text-xs font-semibold transition ${realtime ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}
            title="Show revenue, Kaimera and net based on actual receipts + journal entries instead of the projected fee tiers"
            data-testid="budget-realtime-toggle"
          >
            <input
              type="checkbox" checked={realtime}
              onChange={(e) => setRealtime(e.target.checked)}
              className="w-4 h-4 accent-emerald-600"
            />
            <span className={`w-1.5 h-1.5 rounded-full ${realtime ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`}></span>
            Real time {realtime && live && <span className="text-[10px] font-normal text-slate-400">· {live.receipt_count} receipt{live.receipt_count === 1 ? "" : "s"}</span>}
          </label>
          <button onClick={saveDelegateConfig} className="btn-primary" data-testid="budget-save-btn"><Save className="w-4 h-4" /> Save & Mark Complete</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={exportExcel} className="btn-ghost text-sm" data-testid="budget-export-btn"><FileSpreadsheet className="w-4 h-4" /> Download Excel</button>
        <label className="btn-ghost text-sm cursor-pointer" data-testid="budget-import-btn">
          <Upload className="w-4 h-4" /> Upload Excel
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => importExcel(e.target.files?.[0])} />
        </label>
      </div>

      <div className="grid lg:grid-cols-4 gap-4 mb-6">
        <Stat label={useLive ? "Revenue (live)" : "Total Revenue"} value={totalRev} color="emerald" sub={useLive ? `₹${live.online_revenue.toLocaleString()} online + ₹${live.cash_revenue.toLocaleString()} cash + other incomes` : "Incl. all fee tiers"} />
        <Stat label={useLive ? "Expenses (live)" : "Total Expenses"} value={totalExp} color="rose" sub={useLive ? "Recorded expenses + Kaimera share retained" : "Incl. Kaimera fee"} />
        <Stat label={useLive ? "Net (live)" : "Net Profit"} value={net} color={net >= 0 ? "orange" : "rose"} sub={net >= 0 ? "Healthy margin" : "In red"} />
        <Stat label={useLive ? "Kaimera Fee (retained)" : "Kaimera Fee (segregated)"} value={shownKaimera} color="indigo" sub={`${shownDelegateCount} delegate(s)${useLive ? " · from receipts" : ""}`} />
      </div>

      {/* Missing-payments diagnostic — only in real-time mode. Receipts
          that look like this event but aren't linked to it (rescued /
          legacy rows) would otherwise silently undercount live revenue. */}
      {useLive && ((live.unassigned_receipts?.count || 0) + (live.name_matched_elsewhere?.count || 0)) > 0 && (
        <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm" data-testid="realtime-missing-warning">
          <div className="font-bold text-amber-800">⚠ Some payments aren't counted in this event's live totals</div>
          <ul className="mt-1 space-y-0.5 text-amber-900/90 text-xs">
            {(live.unassigned_receipts?.count || 0) > 0 && (
              <li>• {live.unassigned_receipts.count} receipt(s) worth ₹{(live.unassigned_receipts.amount_paise / 100).toLocaleString("en-IN")} have <b>no event linked</b>: {live.unassigned_receipts.sample.map((s) => s.receipt_number).filter(Boolean).join(", ")}{(live.unassigned_receipts.count > 10) ? "…" : ""}</li>
            )}
            {(live.name_matched_elsewhere?.count || 0) > 0 && (
              <li>• {live.name_matched_elsewhere.count} receipt(s) worth ₹{(live.name_matched_elsewhere.amount_paise / 100).toLocaleString("en-IN")} carry this event's name but a <b>different event link</b>: {live.name_matched_elsewhere.sample.map((s) => s.receipt_number).filter(Boolean).join(", ")}{(live.name_matched_elsewhere.count > 10) ? "…" : ""}</li>
            )}
          </ul>
          <div className="text-[11px] text-amber-700 mt-1.5">The accountant can reassign these from Accounts → Receipts so they count toward this event.</div>
        </div>
      )}

      {/* Fee Tiers panel */}
      <div className="dashboard-card p-5 mb-5" data-testid="fee-tiers-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-bold flex items-center gap-2"><Layers className="w-4 h-4 text-indigo-600" /> Fee Tiers</h4>
            <p className="text-xs text-slate-500 mt-0.5">Add a row for every fee category (School delegates, Individual delegates, Host school, Faculty, etc.). Kaimera per-delegate can be overridden per tier — leave blank to inherit the default below.</p>
          </div>
          <button onClick={addTier} className="btn-ghost text-sm" data-testid="add-fee-tier-btn"><Plus className="w-4 h-4" /> Add Tier</button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100">
                <th className="py-2 pr-2">Tier Label</th>
                <th className="py-2 pr-2 text-right w-28">Fee ₹</th>
                <th className="py-2 pr-2 text-right w-24"># Students</th>
                <th className="py-2 pr-2 text-right w-40">Kaimera Override ₹ <span className="text-slate-300 font-normal">(optional)</span></th>
                <th className="py-2 pr-2 text-right w-32">Tier Revenue</th>
                <th className="py-2 pr-2 text-right w-32">Tier Kaimera</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t, idx) => {
                const rev = (parseFloat(t.fee_amount) || 0) * (parseInt(t.student_count) || 0);
                const per = (t.kaimera_fee_override === "" || t.kaimera_fee_override == null) ? (parseFloat(kaimeraFee) || 0) : (parseFloat(t.kaimera_fee_override) || 0);
                const km = per * (parseInt(t.student_count) || 0);
                return (
                  <tr key={t.tier_id} className="border-b border-slate-50" data-testid={`fee-tier-row-${idx}`}>
                    <td className="py-2 pr-2">
                      <input className="input-pill py-1.5 text-sm" placeholder="e.g. School Delegate" value={t.label} onChange={(e) => updateTier(idx, { label: e.target.value })} data-testid={`fee-tier-${idx}-label`} />
                    </td>
                    <td className="py-2 pr-2">
                      <input type="number" min={0} className="input-pill py-1.5 text-sm text-right" value={t.fee_amount} onChange={(e) => updateTier(idx, { fee_amount: e.target.value })} data-testid={`fee-tier-${idx}-fee`} />
                    </td>
                    <td className="py-2 pr-2">
                      <input type="number" min={0} className="input-pill py-1.5 text-sm text-right" value={t.student_count} onChange={(e) => updateTier(idx, { student_count: e.target.value })} data-testid={`fee-tier-${idx}-count`} />
                    </td>
                    <td className="py-2 pr-2">
                      <input type="number" min={0} placeholder="inherits default" className="input-pill py-1.5 text-sm text-right" value={t.kaimera_fee_override ?? ""} onChange={(e) => updateTier(idx, { kaimera_fee_override: e.target.value === "" ? null : e.target.value })} data-testid={`fee-tier-${idx}-kaimera`} />
                    </td>
                    <td className="py-2 pr-2 text-right font-semibold text-emerald-700">₹{rev.toLocaleString()}</td>
                    <td className="py-2 pr-2 text-right font-semibold text-indigo-700">₹{km.toLocaleString()}</td>
                    <td className="py-2 text-right">
                      <button onClick={() => delTier(idx)} className="text-rose-500 hover:bg-rose-50 p-1 rounded" title="Remove tier" data-testid={`fee-tier-${idx}-del`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200">
                <td className="pt-2 pl-1 font-bold text-slate-700">Totals</td>
                <td></td>
                <td className="pt-2 text-right font-bold text-slate-700">{totalDelegates}</td>
                <td></td>
                <td className="pt-2 text-right font-display font-black text-emerald-700">₹{delegateRev.toLocaleString()}</td>
                <td className="pt-2 text-right font-display font-black text-indigo-700">₹{kaimera.toLocaleString()}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100 grid md:grid-cols-2 gap-3 items-end">
          <div>
            <label className="text-xs text-slate-500">Default Kaimera fee per delegate (₹) <span className="text-slate-400">— set by Regional PM / Admin, used when a tier leaves its override blank</span></label>
            <input type="number" min={0} className="input-pill" value={kaimeraFee} onChange={(e) => setKaimeraFee(e.target.value)} data-testid="budget-kaimera-default" />
          </div>
          <div className="text-xs text-slate-500 text-right">
            <div>Total delegate revenue: <b className="text-emerald-700">₹{delegateRev.toLocaleString()}</b></div>
            <div>Total Kaimera (segregated expense): <b className="text-indigo-700">₹{kaimera.toLocaleString()}</b></div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Ledger
          title={realtime ? "Incomes (real-time)" : "Incomes"}
          kind="income"
          accent="emerald"
          Icon={TrendingUp}
          items={activeIncomes}
          onAdd={(item) => addItem("income", item)}
          onUpdate={(i, p) => updateItem("income", i, p)}
          onDel={(i) => delItem("income", i)}
          editingIdx={editingIncome}
          setEditingIdx={setEditingIncome}
          testidPrefix="income"
          modeHint={realtime ? "Only shows in Real-time mode — separate from your projected ledger." : "Projected — switch on Real time to log actual incomes separately."}
        />
        <Ledger
          title={realtime ? "Expenses (real-time)" : "Expenses"}
          kind="expense"
          accent="rose"
          Icon={TrendingDown}
          items={activeExpenses}
          onAdd={(item) => addItem("expense", item)}
          onUpdate={(i, p) => updateItem("expense", i, p)}
          onDel={(i) => delItem("expense", i)}
          editingIdx={editingExpense}
          setEditingIdx={setEditingExpense}
          testidPrefix="expense"
          modeHint={realtime ? "Only shows in Real-time mode — separate from your projected ledger." : "Projected — switch on Real time to log actual expenses separately."}
        />
      </div>
    </div>
  );
}

function Stat({ label, value, color, sub }) {
  const palette = {
    emerald: "from-green-50 to-emerald-50 text-emerald-700",
    rose: "from-rose-50 to-pink-50 text-rose-700",
    orange: "from-orange-50 to-amber-50 text-orange-700",
    indigo: "from-indigo-50 to-blue-50 text-indigo-700",
  }[color] || "from-slate-50 to-slate-100 text-slate-700";
  return (
    <div className={`dashboard-card p-5 bg-gradient-to-br ${palette}`}>
      <div className="text-xs font-bold uppercase tracking-wider">{label}</div>
      <div className="text-3xl font-display font-black mt-1">₹{(value || 0).toLocaleString()}</div>
      <div className="text-xs mt-1 opacity-80">{sub}</div>
    </div>
  );
}

function Ledger({ title, kind, accent, Icon, items, onAdd, onUpdate, onDel, editingIdx, setEditingIdx, testidPrefix, modeHint }) {
  const [form, setForm] = useState({ label: "", quantity: 1, amount: "" });
  const [draftEdit, setDraftEdit] = useState(null);
  const inputRef = useRef(null);

  const submit = (e) => {
    e.preventDefault();
    onAdd(form);
    setForm({ label: "", quantity: 1, amount: "" });
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const startEdit = (i) => { setEditingIdx(i); setDraftEdit({ ...items[i] }); };
  const cancelEdit = () => { setEditingIdx(null); setDraftEdit(null); };
  const commitEdit = async (i) => {
    await onUpdate(i, { label: draftEdit.label, quantity: parseFloat(draftEdit.quantity) || 1, amount: parseFloat(draftEdit.amount) || 0 });
    setEditingIdx(null); setDraftEdit(null);
  };

  const accentBar = { emerald: "bg-emerald-500", rose: "bg-rose-500" }[accent] || "bg-slate-400";
  const accentText = { emerald: "text-emerald-700", rose: "text-rose-700" }[accent] || "text-slate-700";

  const total = items.reduce((s, it) => s + (it.amount * it.quantity), 0);

  return (
    <div className="dashboard-card p-5" data-testid={`ledger-${kind}`}>
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-display font-bold text-lg flex items-center gap-2"><Icon className={`w-4 h-4 ${accentText}`} /> {title}</h4>
        <div className={`text-sm font-bold ${accentText}`}>₹{total.toLocaleString()}</div>
      </div>
      {modeHint && <p className="text-[11px] text-slate-400 mb-3">{modeHint}</p>}

      {/* Add-row form */}
      <form onSubmit={submit} className={`p-3 rounded-xl bg-slate-50 mb-3 border-l-4 ${accentBar.replace('bg-', 'border-')}`}>
        <div className="grid grid-cols-12 gap-2">
          <input
            ref={inputRef}
            placeholder={kind === "income" ? "e.g. Sponsorship — Coca Cola" : "e.g. Catering"}
            className="input-pill py-2 text-sm col-span-6"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            data-testid={`${testidPrefix}-label-input`}
          />
          <input
            type="number" min={1} placeholder="Qty"
            className="input-pill py-2 text-sm col-span-2"
            value={form.quantity}
            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            data-testid={`${testidPrefix}-qty-input`}
          />
          <input
            type="number" min={0} placeholder="Value ₹"
            className="input-pill py-2 text-sm col-span-3"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            data-testid={`${testidPrefix}-amount-input`}
          />
          <button type="submit" className="btn-primary col-span-1 py-2 px-0" title="Add" data-testid={`${testidPrefix}-add-btn`}><Plus className="w-4 h-4" /></button>
        </div>
        {form.label && form.amount > 0 && (
          <div className="text-[11px] text-slate-500 mt-1.5">
            Will add: <b>{form.label}</b> × {form.quantity || 1} = <b className={accentText}>₹{((parseFloat(form.amount) || 0) * (parseFloat(form.quantity) || 1)).toLocaleString()}</b>
          </div>
        )}
      </form>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="py-2 pl-1">Name</th>
              <th className="py-2 text-right w-16">Qty</th>
              <th className="py-2 text-right w-24">Value ₹</th>
              <th className="py-2 text-right w-28">Subtotal</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="5" className="text-center text-slate-400 py-6 text-sm">No {kind}s yet — add one above.</td></tr>}
            {items.map((it, i) => (
              editingIdx === i ? (
                <tr key={it.item_id || i} className="bg-orange-50">
                  <td className="py-1.5 pl-1"><input className="input-pill py-1 text-xs" value={draftEdit.label} onChange={(e) => setDraftEdit({ ...draftEdit, label: e.target.value })} /></td>
                  <td className="py-1.5"><input type="number" className="input-pill py-1 text-xs w-full" value={draftEdit.quantity} onChange={(e) => setDraftEdit({ ...draftEdit, quantity: e.target.value })} /></td>
                  <td className="py-1.5"><input type="number" className="input-pill py-1 text-xs w-full" value={draftEdit.amount} onChange={(e) => setDraftEdit({ ...draftEdit, amount: e.target.value })} /></td>
                  <td className={`py-1.5 text-right font-semibold ${accentText}`}>₹{((parseFloat(draftEdit.amount) || 0) * (parseFloat(draftEdit.quantity) || 1)).toLocaleString()}</td>
                  <td className="py-1.5 text-right">
                    <button onClick={() => commitEdit(i)} className="text-emerald-600 text-xs font-bold mr-1">✓</button>
                    <button onClick={cancelEdit} className="text-slate-400 text-xs">✕</button>
                  </td>
                </tr>
              ) : (
                <tr key={it.item_id || i} className="border-b border-slate-50 hover:bg-slate-50/40">
                  <td className="py-2 pl-1 cursor-pointer" onClick={() => startEdit(i)} data-testid={`${testidPrefix}-row-${i}-label`}>
                    <div className="font-medium text-slate-800">{it.label}</div>
                    {it.notes && <div className="text-[10px] text-slate-400">{it.notes}</div>}
                  </td>
                  <td className="py-2 text-right text-slate-600">{it.quantity}</td>
                  <td className="py-2 text-right text-slate-600">₹{(it.amount || 0).toLocaleString()}</td>
                  <td className={`py-2 text-right font-semibold ${accentText}`}>₹{((it.amount || 0) * (it.quantity || 1)).toLocaleString()}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => startEdit(i)} className="text-xs text-orange-600 hover:underline mr-2">edit</button>
                    <button onClick={() => onDel(i)} className="text-rose-500" data-testid={`${testidPrefix}-del-${i}`}><Trash2 className="w-3.5 h-3.5 inline" /></button>
                  </td>
                </tr>
              )
            ))}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-200">
                <td colSpan="3" className="pt-2 font-bold text-slate-700 pl-1">Total</td>
                <td className={`pt-2 text-right font-display font-black ${accentText}`}>₹{total.toLocaleString()}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
